interface EnvironmentInfo {
    browser: string;
    platform: string;
};

interface SubscriptionPayload {
    email: string;
    listId: string[];
};

export const getEnvironment = (): EnvironmentInfo => {
    const userAgent = navigator.userAgent;
    const platform = navigator.platform;

    // --- Detect Browser ---
    let browser = 'Unknown';
    if (/Edg/i.test(userAgent)) browser = 'Microsoft Edge';
    else if (/OPR|Opera/i.test(userAgent)) browser = 'Opera';
    else if (/Chrome/i.test(userAgent)) browser = 'Google Chrome';
    else if (/Safari/i.test(userAgent)) browser = 'Safari';
    else if (/Firefox/i.test(userAgent)) browser = 'Mozilla Firefox';
    else if (/MSIE|Trident/i.test(userAgent)) browser = 'Internet Explorer';

    // --- Detect Platform / OS ---
    let os = 'Unknown';
    if (/Win/i.test(platform)) os = 'Windows';
    else if (/Mac/i.test(platform)) os = 'macOS';
    else if (/Linux/i.test(platform)) os = 'Linux';
    else if (/iPhone|iPad|iPod/i.test(userAgent)) os = 'iOS';
    else if (/Android/i.test(userAgent)) os = 'Android';

    return { browser, platform: os };
};

export const getPlatformIcon = (): string => {
    const { platform } = getEnvironment();

    let icon: string = '';

    switch (platform) {
        case 'iOS':
        case 'macOS': icon = 'apple'; break;
        case 'Linux': icon = 'linux'; break;
        case 'Android': icon = 'android'; break;
        case 'Windows': icon = 'windows'; break;
    };

    return icon;
};

const getArchitecture = (): 'arm64' | 'arm' | 'x64' | 'x86' | 'unknown' => {
    const ua = navigator.userAgent.toLowerCase();
    const plat = (navigator.platform || '').toLowerCase();

    if (/\b(aarch64|arm64)\b/.test(ua)) return 'arm64';
    if (/\barmv7|\barm(?=\b|[_-])/.test(ua)) return 'arm';
    if (/\bx86_64|\bx64\b|\bwin64\b|\bamd64\b/.test(ua) || /x86_64|win64|amd64/i.test(plat))
        return 'x64';
    if (/\bi686|\bx86\b|\bwin32\b/.test(ua) || /i686|x86|win32/i.test(plat)) return 'x86';
    if (/mac/.test(plat)) return 'x64';
    if (/android/.test(ua)) return 'unknown';

    return 'unknown';
};

const toUrl = (val?: string | { url?: string } | null): string => {
    if (!val) return '';
    if (typeof val === 'string') return val || '';
    return val.url || '';
};

export const fetchLatestReleaseUrl = async (): Promise<any[]> => {
    const endpoint = 'https://publish-releases.anytype.io/api/v1/latestRelease';
    const { platform } = getEnvironment();
    const arch = getArchitecture();

    try {
        const response = await fetch(endpoint, { headers: { Accept: 'application/json' } });
        if (!response.ok) throw new Error(`Failed to fetch releases: ${response.statusText}`);

        let data = await response.json();
        data = data.LatestReleasesLinks;

        const ret = [];

        switch (platform) {
            case 'Android': {
                if (data['ANDROID_GOOGLE_PLAY']) ret.push({ name: 'Google Play', event: 'Android_store', url: toUrl(data['ANDROID_GOOGLE_PLAY']) });
                if (data['ANDROID_ARM32']) ret.push({ name: 'Android (ARM32)', event: 'Android_arm32', url: toUrl(data['ANDROID_ARM32']) });
                if (data['ANDROID_ARM64']) ret.push({ name: 'Android (ARM64)', event: 'Android_arm64', url: toUrl(data['ANDROID_ARM64']) });
                if (data['ANDROID_UNIVERSAL']) ret.push({ name: 'Android (Universal)', event: 'Android_universal', url: toUrl(data['ANDROID_UNIVERSAL']) });
                break;
            };
            case 'iOS': {
                if (data['IOS']) ret.push({ name: 'App Store', event: 'iOS', url: toUrl(data['IOS']) });
                break;
            };
            case 'Windows': {
                if (data['WINDOWS']) ret.push({ name: 'Windows', event: 'Windows', url: toUrl(data['WINDOWS']) });
                break;
            };
            case 'macOS': {
                if (data['MACOS_ARM64']) ret.push({ name: 'Apple Silicon (ARM64)', event: 'Mac_apple_silicon_arm64', url: toUrl(data['MACOS_ARM64']) });
                if (data['MACOS_INTEL_X64']) ret.push({ name: 'Intel (X64)', event: 'Mac_intel_x64', url: toUrl(data['MACOS_INTEL_X64']) });
                break;
            };
            case 'Linux': {
                if (data['LINUX_APP_IMAGE']) ret.push({ name: 'App Image', event: 'Linux_appimage', url: toUrl(data['LINUX_APP_IMAGE']) });
                if (data['LINUX_DEBIAN']) ret.push({ name: 'Debian', event: 'Linux_debian', url: toUrl(data['LINUX_DEBIAN']) });
                if (data['LINUX_RPM']) ret.push({ name: 'RPM', event: 'linux_rpm', url: toUrl(data['LINUX_RPM']) });
                break;
            };
        };

        return ret;
    } catch (error) {
        console.error('Error fetching latest release URL:', error);
        return [];
    };

    return [];
};

export const matchEmail = (v: string) => {
    v = String(v || '');

    if (!/@/.test(v) || (v.length < 5)) {
        return '';
    };

    const uc = '\\P{Script_Extensions=Latin}';
    const m = v.match(new RegExp(`^[-\\.\\w${uc}]+@([-\\.\\w${uc}]+\\.)+[-\\w${uc}]{2,12}$`, 'gu'));

    return String(((m && m.length) ? m[0] : '') || '').trim();
};

export const subscribeEmail = async (
    payload: SubscriptionPayload
): Promise<any | null> => {
    try {
        const endpoint = 'https://anytype.io/v3/marketing/contacts';
        const { listId, email } = payload;
	const response = await fetch(endpoint, {
	    method: 'PUT',
	    headers: {
		'Content-Type': 'application/json',
		'Accept': 'application/json',
	    },
	    body: JSON.stringify({
                list_ids: listId,
                contacts: [
                    {
                        email,
                    },
                ]
            }),
	});

	if (!response.ok) {
	    throw new Error(`Request failed: ${response.status} ${response.statusText}`);
	};

	return await response.json();
    } catch (error) {
	console.error('Error updating subscription data:', error);
	return null;
    };
};

export const openInNewTab = (url: string) => {
    const newWindow = window.open(url, '_blank', 'noopener,noreferrer');

    if (!newWindow) {
        return;
    };

    newWindow.opener = null
};
